import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import FeedHeading from 'buyside/components/presentation/Feed/FeedHeading/feedHeading';
import IdeaUpdateAttachments from './IdeaUpdateAttachments/ideaUpdateAttachments';
import IdeaUpdateDelta from './IdeaUpdateDelta/ideaUpdateDelta';
import IdeaUpdateImpact from './IdeaUpdateImpact/ideaUpdateImpact';
import Link, { LINK_TYPES } from 'buyside/components/presentation/Link/link';
import YouTubePlayer from 'buyside/components/presentation/YouTubePlayer/youtubePlayer';

import { ideaUpdateUrl, ideaUrl } from 'buyside/util/urlHelper';

import { ActivityFeedIdeaUpdateFragment } from 'interfaces/graphql-typings';
import { FeedItemComponentType } from 'interfaces/feed/feedItems';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import IdeaReadingListButton from 'buyside/components/presentation/IdeaReadingListButton/ideaReadingListButton';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons/faLightbulb';
import { useIsPublic } from 'buyside/util/hooks/useWindowLocation';

const truncateText = (text?: string): string => {
  if (!text) return '';
  return text.substr(0, 300) + '...';
};

import styles from './ideaUpdateStyles.scss';

const IdeaUpdate: React.FC<{
  ideaUpdate: ActivityFeedIdeaUpdateFragment;
} & FeedItemComponentType> = ({ ideaUpdate, handleClick, currentUser, eventVerb, createdAt }) => {
  const isPublic = useIsPublic();

  const changesHash: any[] = JSON.parse(ideaUpdate.changesHash);
  const redacted = !ideaUpdate?.idea?.canCurrentUserViewIdeaFilterDetails;

  let ideaUpdateText = '';
  if (ideaUpdate.text) {
    ideaUpdateText = ideaUpdate.text.replace(/&amp;/g, '&');
  }

  const { closed, impact, youtubeIds } = ideaUpdate;

  const isCurrentUser = currentUser?.id === ideaUpdate?.user?.id;

  return (
    <div>
      {eventVerb === 'PUBLISH_IDEA_UPDATE' && (
        <div className={styles.feedItemHeader}>
          <FeedHeading
            createdAt={createdAt}
            headerType={eventVerb}
            idea={ideaUpdate?.idea}
            showUserDetails={
              (!ideaUpdate.idea?.anonymous && Boolean(currentUser?.canViewUserDetails)) ||
              isCurrentUser
            }
            user={ideaUpdate.user}
          />
        </div>
      )}
      <div className={styles.ideaUpdate}>
        <div className={styles.impactSection}>
          <IdeaUpdateImpact closed={closed} impact={impact} />
          <div className={styles.updatePostedDate}>
            Update Created: {moment(ideaUpdate.createdAt).format('l')}
          </div>
        </div>
        {changesHash && Boolean(changesHash.length) && (
          <div className={styles.changesSection}>
            {changesHash.map(changeHash => {
              return (
                <IdeaUpdateDelta
                  changesHash={changeHash}
                  key={changeHash.value}
                  redacted={redacted}
                />
              );
            })}
          </div>
        )}
        {ideaUpdate.ideaAttachments && ideaUpdate.ideaAttachments.edges && (
          <IdeaUpdateAttachments attachments={ideaUpdate.ideaAttachments} />
        )}
        <div className={classNames(styles.ideaUpdateTextWrap, { [styles.blur]: redacted })}>
          {ideaUpdate.idea?.id && handleClick && (
            <Link
              to={redacted ? null : ideaUpdateUrl(ideaUpdate.idea?.id, ideaUpdate.id)}
              onClick={handleClick}
              type={LINK_TYPES.PLAIN}
            >
              <blockquote className={styles.ideaUpdateText}>
                {truncateText(ideaUpdateText)}
              </blockquote>
            </Link>
          )}
        </div>
        {ideaUpdate.idea?.id && (
          <div className={styles.actionsArea}>
            <Button
              text="View Idea"
              faIcon={faLightbulb}
              type={BUTTON_TYPES.PRIMARY}
              size={BUTTON_SIZES.SMALL}
              url={ideaUrl(ideaUpdate.idea?.id)}
            />
            {!isPublic && (
              <IdeaReadingListButton
                button
                buttonSize={BUTTON_SIZES.SMALL}
                ideaId={ideaUpdate.idea?.id}
              />
            )}
          </div>
        )}
        {youtubeIds && youtubeIds.map(id => <YouTubePlayer key={id} height={351} videoId={id} />)}
      </div>
    </div>
  );
};

export default IdeaUpdate;
